<template>
  <CustomModal
    :id="`comment-changed-${comment.id}`"
    modalClass="comment-changed-modal"
    width="75%"
  >
    <template #header>
      <div class="comment-changed-modal__header">
        <CommentOwner
          :user="comment.owner"
          userNameWeight="400"
        >
          <template #username="{ userName }">
            <BaseHeading level="4">
              {{ userName }} <span class="heading-level-3">edited Comment</span>
            </BaseHeading>
          </template>
        </CommentOwner>
        <BaseSubHeading
          size="large"
          weight="400"
          color="monochrome-05"
          darkColor="monochrome-04"
        >
          {{ comment.timeAndDateReversed }}
        </BaseSubHeading>
      </div>
    </template>
    <template #body>
      <div class="comment-changed-modal__body">
        <span class="comment-changed-modal__column">
          <BaseHeading
            class="comment-changed-modal__label"
            level="4"
          >
            Before:
          </BaseHeading>
          <div class="comment-changed-modal__card">
            <div class="custom-scroll comment-changed-modal__card__content">
              <div
                v-if="mediaFilesBefore?.length"
                class="comment-changed-modal__files"
                :style="mediaFilesBefore.length === 1 ? { 'max-width': '260px' } : {}"
              >
                <FileCommentItem
                  v-for="item in mediaFilesBefore"
                  :key="item.id"
                  :item="item"
                  :isRemoved="item.deleted"
                  :projectScoped="projectScoped"
                />
              </div>
              <div
                v-if="before"
                class="comment-changed-modal__box"
              >
                <CommentHistoryContent
                  :comment="before"
                  :comments="[]"
                  :demoVideoId="comment.demoVideoId"
                  :demoVideoVersionMap="demoVideoVersionMap"
                  :projectScoped="projectScoped"
                />
                <div class="comment-changed-modal__footer">
                  <div class="comment-changed-modal__actions">
                    <ReactionList
                      :reactions="before.reactionsWithUserPreviews"
                      :myReactionType="myReactionTypeBefore"
                    />
                  </div>
                  <div class="comment-changed-modal__info">
                    <BaseSubHeading
                      v-tooltip.bottom="{ content: comment.timeAndDate, theme: 'info-tooltip' }"
                      size="small"
                      weight="400"
                      color="monochrome-05"
                      darkColor="monochrome-04"
                      class="comment-changed-modal__date"
                    >
                      {{ comment.timeAgo }}
                    </BaseSubHeading>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </span>
        <span class="comment-changed-modal__column">
          <BaseHeading
            class="comment-changed-modal__label"
            level="4"
          >
            After:
          </BaseHeading>
          <div class="comment-changed-modal__card">
            <div class="custom-scroll comment-changed-modal__card__content">
              <div
                v-if="mediaFilesAfter?.length"
                class="comment-changed-modal__files"
                :style="mediaFilesAfter.length === 1 ? { 'max-width': '260px' } : {}"
              >
                <FileCommentItem
                  v-for="item in mediaFilesAfter"
                  :key="item.id"
                  :item="item"
                  :isRemoved="item.deleted"
                  :projectScoped="projectScoped"
                />
              </div>
              <div
                v-if="after"
                class="comment-changed-modal__box"
              >
                <CommentHistoryContent
                  :comment="after"
                  :comments="[]"
                  :demoVideoId="comment.demoVideoId"
                  :demoVideoVersionMap="demoVideoVersionMap"
                  :projectScoped="projectScoped"
                />
                <div class="comment-changed-modal__footer">
                  <div class="comment-changed-modal__actions">
                    <ReactionList
                      :reactions="after.reactionsWithUserPreviews"
                      :myReactionType="myReactionTypeAfter"
                    />
                  </div>
                  <div class="comment-changed-modal__info">
                    <BaseSubHeading
                      v-tooltip.bottom="{ content: comment.timeAndDate, theme: 'info-tooltip' }"
                      size="small"
                      weight="400"
                      color="monochrome-05"
                      darkColor="monochrome-04"
                      class="comment-changed-modal__date"
                    >
                      {{ comment.timeAgo }}
                    </BaseSubHeading>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </span>
      </div>
    </template>

    <template #footer="{ hide }">
      <ControlButtons
        :hideCancel="true"
        @save="hide"
      >
        <template #save> Ok </template>
      </ControlButtons>
    </template>
  </CustomModal>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Prop } from "vue-property-decorator"
import { Getter } from "s-vuex-class"

import CommentHistoryContent from "@/components/Comment/CommentContent/CommentHistoryContent.vue"
import ControlButtons from "@/components/ControlButtons/index.vue"
import Editor from "@/components/Editor/index.vue"
import ReactionList from "@/components/Emoji/ReactionList.vue"
import FileCommentItem from "@/components/File/FileCommentItem.vue"
import CustomModal from "@/components/Modal/index.vue"
import CommentOwner from "@/components/User/index.vue"

import { isImage } from "@/helpers/isImg"
import { isVideo } from "@/helpers/isVideo"
import { Comment, EmojiType, Item, User } from "@/models"
import { Getters } from "@/store/getters"

import type { DemoVideoVersionInfo } from "@/composables/useDemoVideo"

@Options({
  name: "ModalItemDescriptionChanged",
  components: {
    CustomModal,
    ControlButtons,
    Editor,
    CommentOwner,
    CommentHistoryContent,
    FileCommentItem,
    ReactionList
  }
})
export default class extends Vue {
  @Prop({ required: true }) comment: Comment
  @Prop({ default: () => ({}) }) demoVideoVersionMap: Record<string, DemoVideoVersionInfo>
  @Prop({ default: true }) projectScoped: boolean
  @Getter(Getters.GET_ITEM) item: Item
  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User

  get before() {
    return this.comment.commentAuditRecord?.before
  }

  get after() {
    return this.comment.commentAuditRecord?.after
  }

  get mediaFilesBefore() {
    if (!this.before) return []
    return this.before.fileMetadata?.filter(item => isImage(item.name) || isVideo(item.name))
  }

  get mediaFilesAfter() {
    if (!this.after) return []
    return this.after.fileMetadata?.filter(item => isImage(item.name) || isVideo(item.name))
  }

  get myReactionTypeBefore() {
    if (!this.before) return null
    for (const [reactionType, users] of Object.entries(this.before.reactionsWithUserPreviews)) {
      if (users.some(user => user.id === this.currentUser.id)) {
        return reactionType as EmojiType
      }
    }
    return null
  }

  get myReactionTypeAfter() {
    if (!this.after) return null
    for (const [reactionType, users] of Object.entries(this.after.reactionsWithUserPreviews)) {
      if (users.some(user => user.id === this.currentUser.id)) {
        return reactionType as EmojiType
      }
    }
    return null
  }
}
</script>

<style lang="scss">
.comment-changed-modal__card__content {
  padding-right: 4px;

  &::-webkit-scrollbar-track {
    border-radius: var(--radius-item);
    scrollbar-width: 1px;
  }

  &::-webkit-scrollbar {
    width: 4px;
    border-radius: var(--radius-item);
  }

  &::-webkit-scrollbar-thumb {
    border: var(--accent-01);
    border-radius: var(--radius-item);

    &:hover {
      border: var(--accent-01);
    }
  }
}
</style>

<style scoped lang="scss">
@import "@/assets/scss/utils/mixins/_ellipsis";
@import "@/assets/scss/utils/mixins/_flex";

.comment-changed-modal {
  &__header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin-bottom: var(--step6);
  }

  &__body {
    @include flex;

    gap: var(--step8);
  }

  &__column {
    @include flex($direction: column);

    overflow: hidden;
    width: 100%;
    height: 100%;
    gap: var(--step4);
  }

  &__card {
    overflow: hidden;
    height: 59vh;
    padding: var(--step6);
    border-radius: var(--radius-section);
    background: linear-gradient(120.7deg, rgba(206, 148, 255, 0.2) 0%, rgba(0, 224, 242, 0.2) 100%);

    &__content {
      height: 100%;
      overflow-y: auto;
    }
  }

  &__box {
    overflow: hidden;
    min-width: 200px;
    max-width: 100%;
    padding: var(--step4);
    border-radius: 0 0 var(--step4) var(--step4);
    background: white;
  }

  &__footer {
    @include flex($justify: space-between, $align: center);
  }

  &__files {
    flex-wrap: wrap;
    margin-bottom: var(--step1);
    gap: var(--step1);

    @include flex($align: unset);
  }
}

.dark {
  .comment-changed-modal {
    &__box {
      background: var(--monochrome-24);
      box-shadow: var(--center-01);
    }
  }
}
</style>
